import React from 'react';

/**
 * ErrorBoundary — catches render-time errors below it and shows a friendly
 * fallback UI instead of letting the error bubble up to the root and crash
 * the entire app to a blank screen.
 *
 * Use cases: wrap risky form bodies (e.g. New Task modal that touches
 * deeply nested client data) so a bad shape from the API doesn't take
 * down the whole CRM. The error is logged to the console for debugging.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const FallbackProp = this.props.fallback;
      if (typeof FallbackProp === 'function') {
        return FallbackProp({ error: this.state.error, reset: this.reset });
      }
      const label = this.props.label || 'this section';
      return (
        <div
          role="alert"
          data-testid="error-boundary-fallback"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-5 text-sm text-foreground"
        >
          <div className="font-medium text-destructive mb-1.5">Something went wrong</div>
          <div className="text-muted-foreground mb-3">
            We hit an error rendering {label}. Please close and reopen, or refresh the page.
          </div>
          <button
            type="button"
            onClick={this.reset}
            data-testid="error-boundary-reset"
            className="text-xs px-3 py-1.5 rounded-md border border-border/40 hover:bg-card transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
